/**
 * BoxNotesCard — per-smartbox freeform notes (unique installs / special
 * features) for the box-detail page. Seeded into mttr.smartbox_note (migration
 * 133).
 *
 * PLATFORM-ONLY: the /api/boxes/{scu}/notes endpoint 404s non-platform callers,
 * so the card is gated on the platform role client-side (no request fires for
 * customers/vendors). READING is platform-wide; EDITING (add / edit / pin /
 * delete) additionally requires the operator.triage capability, enforced
 * server-side — a platform viewer without it sees the notes read-only.
 *
 * A note with no notes renders nothing for a read-only viewer, but an operator
 * still sees the card (so they have an entry point to add the first note).
 */
import React, { useCallback, useEffect, useState } from "react";

import { useAuth } from "../../contexts/AuthContext";
import {
  type BoxNote,
  type BoxNoteInput,
  createBoxNote,
  deleteBoxNote,
  fetchBoxNotes,
  updateBoxNote,
} from "../../services/boxDetailApi";

interface BoxNotesCardProps {
  scu: string;
}

// Convenience suggestions for the free-text category field (any string is
// accepted server-side; these just seed a datalist).
const CATEGORY_SUGGESTIONS = [
  "install",
  "feature",
  "power",
  "warning",
  "status",
  "general",
];

interface NoteFormValues {
  note: string;
  category: string;
  is_pinned: boolean;
}

/** Shared add/edit form. Controlled locally; calls onSave with the values. */
const NoteForm: React.FC<{
  initial?: NoteFormValues;
  busy: boolean;
  onSave: (v: NoteFormValues) => void;
  onCancel: () => void;
}> = ({ initial, busy, onSave, onCancel }) => {
  const [note, setNote] = useState(initial?.note ?? "");
  const [category, setCategory] = useState(initial?.category ?? "");
  const [isPinned, setIsPinned] = useState(initial?.is_pinned ?? false);

  const canSave = note.trim().length > 0 && !busy;

  return (
    <div className="border border-secondary-200 rounded p-2 bg-secondary-50 space-y-2">
      <div className="flex gap-2">
        <input
          list="box-note-categories"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="category (optional)"
          maxLength={40}
          className="w-40 text-xs border border-secondary-300 rounded px-2 py-1"
        />
        <datalist id="box-note-categories">
          {CATEGORY_SUGGESTIONS.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        <label className="flex items-center gap-1 text-xs text-secondary-600">
          <input
            type="checkbox"
            checked={isPinned}
            onChange={(e) => setIsPinned(e.target.checked)}
          />
          Pinned
        </label>
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note — e.g. dual-antenna RFID; tablet on Tailscale; prone to damage"
        rows={2}
        maxLength={4000}
        className="w-full text-sm border border-secondary-300 rounded px-2 py-1"
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-xs px-2 py-1 rounded border border-secondary-300 text-secondary-600 hover:bg-secondary-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() =>
            onSave({ note: note.trim(), category: category.trim(), is_pinned: isPinned })
          }
          disabled={!canSave}
          className="text-xs px-2 py-1 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export const BoxNotesCard: React.FC<BoxNotesCardProps> = ({ scu }) => {
  const { token, role, can } = useAuth();
  const isPlatform = role === "platform";
  const canEdit = isPlatform && can("operator.triage");

  const [notes, setNotes] = useState<BoxNote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    if (!token) return;
    const d = await fetchBoxNotes(scu, token);
    setNotes(d.notes);
  }, [scu, token]);

  useEffect(() => {
    if (!isPlatform || !token) return;
    let cancelled = false;
    fetchBoxNotes(scu, token)
      .then((d) => !cancelled && setNotes(d.notes))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [scu, token, isPlatform]);

  // Run a mutation, then reload. Centralizes the busy flag + error handling so
  // every control behaves the same.
  const mutate = useCallback(
    async (fn: () => Promise<unknown>) => {
      if (!token) return;
      setBusy(true);
      setError(null);
      try {
        await fn();
        await reload();
        setAdding(false);
        setEditingId(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [token, reload],
  );

  const onCreate = (v: NoteFormValues) =>
    mutate(() =>
      createBoxNote(
        scu,
        { note: v.note, category: v.category || null, is_pinned: v.is_pinned },
        token!,
      ),
    );

  const onUpdate = (noteId: number, v: NoteFormValues) =>
    mutate(() =>
      updateBoxNote(
        scu,
        noteId,
        { note: v.note, category: v.category || null, is_pinned: v.is_pinned },
        token!,
      ),
    );

  const onTogglePin = (n: BoxNote) =>
    mutate(() => updateBoxNote(scu, n.note_id, { is_pinned: !n.is_pinned }, token!));

  const onDelete = (n: BoxNote) => {
    if (!window.confirm("Delete this note? This can't be undone from the UI.")) return;
    mutate(() => deleteBoxNote(scu, n.note_id, token!));
  };

  // Non-platform: never. Read-only viewer with no notes: render nothing. An
  // operator always sees the card (entry point to add the first note).
  if (!isPlatform) return null;
  const empty = notes !== null && notes.length === 0;
  if (!canEdit && (notes === null || empty)) {
    // read-only viewers: hide until we know there ARE notes
    if (error) {
      return (
        <section className="bg-white border border-secondary-200 rounded-md p-4">
          <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700 mb-1">
            Box notes
          </h2>
          <div className="text-xs text-error-700 bg-error-50 px-2 py-1 rounded">
            Couldn't load notes: {error}
          </div>
        </section>
      );
    }
    return null;
  }

  return (
    <section className="bg-white border border-secondary-200 rounded-md p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700">
          Box notes
        </h2>
        <span className="text-xs text-secondary-400">unique installs / features</span>
      </div>

      {error && (
        <div className="text-xs text-error-700 bg-error-50 px-2 py-1 rounded mb-2">
          {error}
        </div>
      )}

      {notes === null && !error && (
        <div className="text-xs text-secondary-500">loading…</div>
      )}

      {notes !== null && notes.length === 0 && !adding && (
        <div className="text-sm text-secondary-500">
          No notes yet for this box.
        </div>
      )}

      {notes !== null && notes.length > 0 && (
        <ul className="space-y-2">
          {notes.map((n) =>
            editingId === n.note_id ? (
              <li key={n.note_id}>
                <NoteForm
                  initial={{
                    note: n.note,
                    category: n.category ?? "",
                    is_pinned: n.is_pinned,
                  }}
                  busy={busy}
                  onSave={(v) => onUpdate(n.note_id, v)}
                  onCancel={() => setEditingId(null)}
                />
              </li>
            ) : (
              <li
                key={n.note_id}
                className="group flex items-start gap-2 text-sm text-secondary-800"
              >
                {canEdit ? (
                  <button
                    type="button"
                    title={n.is_pinned ? "Unpin" : "Pin"}
                    onClick={() => onTogglePin(n)}
                    disabled={busy}
                    className={`leading-5 ${n.is_pinned ? "" : "opacity-30 hover:opacity-70"}`}
                  >
                    📌
                  </button>
                ) : (
                  n.is_pinned && (
                    <span title="Pinned" className="text-warning-600 leading-5">
                      📌
                    </span>
                  )
                )}
                <div className="min-w-0 flex-1">
                  {n.category && (
                    <span className="inline-block mr-2 px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-600 text-[10px] uppercase tracking-wide align-[1px]">
                      {n.category}
                    </span>
                  )}
                  <span className="whitespace-pre-wrap break-words">{n.note}</span>
                </div>
                {canEdit && (
                  <span className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      title="Edit"
                      onClick={() => setEditingId(n.note_id)}
                      disabled={busy}
                      className="text-xs text-primary-700 hover:text-primary-900"
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      title="Delete"
                      onClick={() => onDelete(n)}
                      disabled={busy}
                      className="text-xs text-error-600 hover:text-error-800"
                    >
                      🗑
                    </button>
                  </span>
                )}
              </li>
            ),
          )}
        </ul>
      )}

      {canEdit && (
        <div className="mt-3">
          {adding ? (
            <NoteForm busy={busy} onSave={onCreate} onCancel={() => setAdding(false)} />
          ) : (
            <button
              type="button"
              onClick={() => {
                setAdding(true);
                setEditingId(null);
              }}
              className="text-xs text-primary-700 hover:text-primary-900 hover:underline"
            >
              + Add note
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default BoxNotesCard;
